import './mail-header-answer-actions.ajscss';

import { AgentDesktopRouter } from '@agent/agent-interface/agent-desktop-router';
import { IEpticaDeprecatedService } from '@agent/core/channel-digit/mail/eptica/eptica-deprecated.service';
import { EpticaButtonActionTypes, EpticaButtonTypes, EpticaData } from '@agent/core/channel-digit/mail/eptica/models/eptica-data';
import {
  HOME_PAGE_NAME,
  IEpticaRequestFrame,
  REQUEST_PAGE_NAME,
  UpdateRecipientFields,
} from '@agent/core/channel-digit/mail/eptica/models/eptica.request.interfaces';
import { IContactDeprecatedService } from '@agent/core/contact/contact-deprecated.service';
import { IKnowledgeBaseService } from '@agent/core/knowledge-base/knowledge-base.service';
import { IResponseAttachment, ISemanticAnalysisResponse } from '@agent/core/knowledge-base/models/knowledge-base-data';
import { ToastDeprecatedService } from '@commons/core/notification/toast-deprecated.service';
import { ModalService } from '@commons/core/ui/modal/modal.service';
import { IModalParamCustomerCardOnlyResult } from '@commons/customer/customer-card-only/model/modal-param-customer-card-only';
import { ICustomerCardsDeprecatedService } from '@commons/customer/customer-cards-deprecated.service';
import { IEpticaExctractAttachment, IEpticaExtractAttachmentAction } from '@commons/history/history-mail/models/eptica-extract';
import { IContact } from '@commons/models/contact';
import { IComponentController, IComponentOptions, ILogService, IPromise, IQService } from 'angular';
import { i18n } from 'i18next';

import { Configuration, None, Option, ProfileService, TranslateService } from '@contact/upgrade';

import { AbstractEpticaController } from './abstract-mail-process';
import templateUrl from './mail-header-answer-actions.template.html';
import { AgentBookinMailInprocessState } from './state/agent-booking-mail-routing.module';
import { KnowledgeBaseOwiService } from '@agent/agent-interface/helpzone/knowledge-base/owi/services/knowledge-base-OWI.service';

const MODULE_NAME = 'contactMailHeaderAnswerActions';

class MailHeaderAnswerActionsCtrl extends AbstractEpticaController<IEpticaRequestFrame> implements IComponentController {
  public name = MODULE_NAME;

  public EpticaButtonTypes = EpticaButtonTypes;

  override responseId: string;

  private displayOWI: boolean = false;
  private displayPrint: boolean = false;
  public thisContact: IContact;
  public title: string = '';
  public epticaRequestId: string = '';
  public actionButtonList: EpticaButtonActionTypes[] = [];
  public allAttachments: IEpticaExctractAttachment[] = [];
  private requestPromise: IPromise<IEpticaRequestFrame>;
  public showClearDraft: boolean = false;
  public replyAllBlackList: string = '@blacklist';

  private isKnowledgeBaseFrameEnabled = this.configuration.isKnowledgeBaseFrameEnabled ?? false;

  constructor(
    override readonly $log: ILogService,
    override readonly $q: IQService,
    private readonly $i18next: i18n,
    override readonly contactDeprecatedService: IContactDeprecatedService,
    private readonly epticaDeprecatedService: IEpticaDeprecatedService,
    private readonly profileService: ProfileService,
    private readonly customerCardsDeprecatedService: ICustomerCardsDeprecatedService,
    private readonly modalService: ModalService,
    override readonly toastDeprecatedService: ToastDeprecatedService,
    private readonly knowledgeBaseService: IKnowledgeBaseService,
    private readonly configuration: Configuration,
    override readonly router: AgentDesktopRouter,
    override readonly translateService: TranslateService,
    private readonly knowledgeBaseOwiService: KnowledgeBaseOwiService
  ) {
    super($log, $q, contactDeprecatedService, toastDeprecatedService, router, translateService);
  }

  mainPromise(): IPromise<IEpticaRequestFrame> {
    return this.requestPromise;
  }

  checkEpticaDataAfterReload(request: IEpticaRequestFrame, epticaData: EpticaData, refuseReason: Option<string>): IPromise<EpticaData> {
    if (epticaData.pageName === HOME_PAGE_NAME) {
      this.endContact(this.responseId, refuseReason).finally(() => request.finish());
    } else if (epticaData.pageName === REQUEST_PAGE_NAME) {
      // Nothing to do, probably a server side validation error from eptica
      this.showActions = true;

      // Hide the answer panel since it's a validation error on another action (like assist, delegate, ..)
      if (this.isState(AgentBookinMailInprocessState.send)) {
        if (this.isState(AgentBookinMailInprocessState.answerAskForHelp)) {
          request.addPrintButtonForAssists();
        }
        request.toggleAnswerPanel(false);
      }

      // Show the request
      request.show();
    } else {
      this.contactDeprecatedService.cancel(this.responseId).finally(() => this.mainPromise().then(r => r.finish()));
    }
    return this.$q.resolve(epticaData);
  }

  // change the disable property of the button matching the id
  changeButtonDisableState(id: string, disableState: boolean) {
    this.getButton(id).map((button: EpticaButtonActionTypes) => (button.disabled = disableState));
  }

  // change the permission (visible or not) of the button matching the id
  changeButtonPermissionState(id: string, visibleState: boolean) {
    this.getButton(id).map((button: EpticaButtonActionTypes) => (button.hasPermission = visibleState));
  }

  // Find the button matching the id
  getButton(id: string): Option<EpticaButtonActionTypes> {
    let rets: EpticaButtonActionTypes[] = [];
    rets = this.actionButtonList.filter(button => button.id === id);
    if (!!rets && rets.length > 0) {
      return Option.of(rets[0]);
    } else {
      // May be in children
      for (let i: number = 0; i < this.actionButtonList.length; i++) {
        rets = this.actionButtonList[i].children.filter(child => child.id === id);
        if (!!rets && rets.length > 0) {
          return Option.of(rets[0]);
        }
      }
    }

    return None;
  }

  openChangeRecipientPopin(customerId: string, selectedUri: string): IPromise<IModalParamCustomerCardOnlyResult> {
    let defer = this.$q.defer<IModalParamCustomerCardOnlyResult>();
    if (customerId) {
      this.customerCardsDeprecatedService.getCustomerCard(customerId).then(customerData => {
        if (customerData?.customer && customerData.addresses) {
          const modalParam = {
            keyboard: false,
            backdrop: false,
            fullscreen: true,
            ctrlKeyboard: true,
            component: 'contactCustomerCardOnly',
            modalId: `contactCustomerCardModalInteraction_${customerData.customer.id}`,
            resolve: {
              customerCard: () => customerData.customer,
              customerAddress: () => customerData.addresses,
              selectedUri: () => selectedUri || '',
            },
          };
          this.modalService.open(modalParam).subscribe(e => {
            if (e.type === 'close') {
              this.modalService.close().then(
                (newIasCustomer: IModalParamCustomerCardOnlyResult) => {
                  defer.resolve(newIasCustomer);
                },
                () => defer.reject()
              );
            }
          });
        }
      });
    }
    return defer.promise;
  }

  clickButton(buttonId: string) {
    let customerId = '';
    this.contactDeprecatedService
      .getContact(this.responseId)
      .then(contact => {
        customerId = contact.customerId || '';
        return this.epticaDeprecatedService.loadRequest({
          requestId: contact.epticaRequestId || 0,
          responseId: this.responseId,
        });
      })
      .then(request => {
        switch (buttonId) {
          case EpticaButtonTypes.PRINT:
            request.print();
            break;
          case EpticaButtonTypes.CHANGE_RECIPIENT:
            const selectedUri = request.getEpticaFieldValue('formAnswerRequest_answerMessage_toRecipientsForView');
            this.openChangeRecipientPopin(customerId, selectedUri).then((newIasCustomer: IModalParamCustomerCardOnlyResult) => {
              request.changeRequestCustomer(newIasCustomer);
              const selectedUris = selectedUri.split(';');
              const filteredUris = selectedUris.filter(uri => uri !== newIasCustomer.uri);
              if (filteredUris.length > 0) {
                const recipients: UpdateRecipientFields = { oldEmail: selectedUri, newEmail: newIasCustomer.uri };
                request.setRecipientFields({ to: filteredUris.concat([newIasCustomer.uri]) });
                request.updateRecipients(recipients);
              }
            });
            break;
          case EpticaButtonTypes.PREVIEW:
            request.preview();
            break;
          case EpticaButtonTypes.PJ:
            // click on the eptica's button
            request.simulateClickOnSelection();
            break;
          case EpticaButtonTypes.CLEAR_DRAFT:
            // clear the editor
            request.clearDraftAction();
            // send OWI's notifiaction
            if (this.isKnowledgeBaseFrameEnabled) {
              this.knowledgeBaseService.notifyClearDraft('' + request.getId()).subscribe();
            } else {
              this.knowledgeBaseOwiService.sendKoDraft('' + request.getId()).subscribe();
            }
            // Disable this button
            this.changeButtonDisableState(buttonId, true);
            break;
          case EpticaButtonTypes.REPLY_ALL:
            request.replyAllAction(this.replyAllBlackList);
            break;
          default:
            break;
        }
      });
  }

  // show the eptica element, hide the current button, show the second button
  showElementAndChangeLabelButton(request: IEpticaRequestFrame, buttonId: string, secondButtonId: string, epticaSelectorToShow: string) {
    // Show Eptica element
    request.showElement(epticaSelectorToShow);

    this.changeLabelButton(buttonId, secondButtonId);
  }

  // hide the eptica element, hide the current button, show the second button
  hideElementAndChangeLabelButton(request: IEpticaRequestFrame, buttonId: string, secondButtonId: string, epticaSelectorToShow: string) {
    // Show Eptica element
    request.hideElement(epticaSelectorToShow);

    this.changeLabelButton(buttonId, secondButtonId);
  }

  changeLabelButton(buttonId: string, secondButtonId: string) {
    // Hide this button
    this.changeButtonPermissionState(buttonId, false);
    // Show the other button
    this.changeButtonPermissionState(secondButtonId, true);
  }

  // show the eptica element and disable tis button
  showElementAndDisableButton(request: IEpticaRequestFrame, buttonId: string, epticaSelectorToShow: string) {
    // Show Eptica element
    request.showElement(epticaSelectorToShow);
    // Disable this button
    this.changeButtonDisableState(buttonId, true);
  }

  removeLinkCallback(file: any) {
    // remove the dropzone file
    this.mainPromise().then(request => {
      request.removeAttachmentFileForAnswer(file.file ? file.file : file);
    });
  }

  $onDestroy(): void {
    this.mainPromise().then(request => request.removePJEvents());
  }

  override $onInit(): void {
    super.$onInit();
    this.replyAllBlackList = this.configuration.eptica?.replyAllBlackList ?? '';
    this.displayOWI = this.profileService.hasPermission('CSR::knowledge_database:display');
    this.displayPrint = this.profileService.hasPermission('CSR::mail_display:print');
    let disableChangeRecipient: boolean = true;

    this.requestPromise = this.contactDeprecatedService
      .getContact(this.responseId)
      .then(contact => {
        this.thisContact = contact;
        return this.epticaDeprecatedService.loadRequest({
          requestId: contact.epticaRequestId || 0,
          responseId: this.responseId,
        });
      })
      .then(request => {
        disableChangeRecipient = !this.contactDeprecatedService.isContactSelected(this.responseId);
        this.epticaRequestId = '' + request.getId();
        if (this.displayPrint) {
          request.expandComments();
        }
        request.doInEpticaFrame(() => {
          const callbackButton = (id: string): void => {
            // Enable the button
            this.changeButtonDisableState(id, false);
          };
          // callback called by the eptica's dropzone
          const callbackAttachmentFiles = (epticafile: IEpticaExctractAttachment): void => {
            if (!!epticafile) {
              if (
                epticafile.action === IEpticaExtractAttachmentAction.ADDED ||
                epticafile.action === IEpticaExtractAttachmentAction.ADDED_BY_DRAFT_LOADING
              ) {
                let updated: boolean = false;
                // update the file if already exists
                this.allAttachments
                  .filter(elem => elem.file === epticafile.file)
                  .forEach(elem => {
                    if (!!epticafile.file) elem.file = epticafile.file;
                    if (!!epticafile.source) elem.source = epticafile.source;
                    if (!!epticafile.sizeInFrame) elem.sizeInFrame = epticafile.sizeInFrame;
                    if (!!epticafile.filename) elem.filename = epticafile.filename;
                    updated = true;
                  });
                // add the file to the component
                if (!updated) this.allAttachments.push(epticafile);
              } else if (epticafile.action === IEpticaExtractAttachmentAction.REMOVED) {
                // remove the file from the component
                this.allAttachments = this.allAttachments.filter(elem => elem.file !== epticafile.file);
              }
            }
          };

          // add the layer of action buttons
          const reply_right: boolean =
            this.profileService.hasPermission('CSR_MAIL_INCOMING::contact:mail_reply_all') &&
            this.profileService.hasPermission('CSR_MAIL_INCOMING::contact:add_cc');

          this.actionButtonList = [
            {
              id: EpticaButtonTypes.REPLY_ALL,
              hasPermission: reply_right,
              label: this.$i18next.t('agent:contact.mailactions.layerAction.buttonReplyAll'),
              label_a11y: this.$i18next.t('agent:contact.mailactions.layerAction.buttonReplyAll'),
              class: 'cng-ico_mail_sendAll',
              callBack: callbackButton,
              disabled: true,
              title: this.$i18next.t('agent:contact.mailactions.layerAction.titleReplyAll'),
              children: [],
            },
            {
              id: EpticaButtonTypes.CHANGE_RECIPIENT,
              hasPermission: this.profileService.hasPermission('CSR_MAIL_INCOMING::contact:change_mail_address'),
              label: this.$i18next.t('agent:contact.mailactions.layerAction.buttonChangeRecipient'),
              label_a11y: this.$i18next.t('agent:contact.mailactions.layerAction.buttonChangeRecipient'),
              class: 'cng-ico_modif_contact',
              callBack: undefined,
              disabled: disableChangeRecipient,
              title: this.$i18next.t('agent:contact.mailactions.layerAction.titleChangeRecipient'),
              children: [],
            },
            {
              id: EpticaButtonTypes.PJ,
              hasPermission: this.profileService.hasPermission('CSR_MAIL_INCOMING::contact:add_attachment'),
              label: this.$i18next.t('agent:contact.mailactions.layerAction.buttonPJ'),
              label_a11y: this.$i18next.t('agent:contact.mailactions.layerAction.buttonPJ'),
              class: 'cng-ico_add_PJ',
              callBack: callbackAttachmentFiles,
              disabled: false,
              title: this.$i18next.t('agent:contact.mailactions.layerAction.titlePJ'),
              children: [],
            },
            {
              id: EpticaButtonTypes.CLEAR_DRAFT,
              hasPermission: true,
              label: this.$i18next.t('agent:contact.mailactions.layerAction.buttonClearDraft'),
              label_a11y: this.$i18next.t('agent:contact.mailactions.layerAction.buttonClearDraft'),
              class: 'cng-ico_erase',
              callBack: undefined,
              disabled: true,
              title: this.$i18next.t('agent:contact.mailactions.layerAction.titleClearDraft'),
              children: [],
            },
            {
              id: EpticaButtonTypes.PREVIEW,
              hasPermission: this.profileService.hasPermission('CSR::mail_display:preview'),
              label: this.$i18next.t('agent:contact.mailactions.layerAction.buttonPreview'),
              label_a11y: this.$i18next.t('agent:contact.mailactions.layerAction.buttonPreview'),
              class: 'cng-ico_preview',
              callBack: undefined,
              disabled: false,
              title: this.$i18next.t('agent:contact.mailactions.layerAction.titlePreview'),
              children: [],
            },
            {
              id: EpticaButtonTypes.PRINT,
              hasPermission: this.displayPrint,
              label: this.$i18next.t('agent:contact.mailactions.layerAction.buttonPrint'),
              label_a11y: this.$i18next.t('agent:contact.mailactions.layerAction.buttonPrint'),
              class: 'cng-ico_print',
              callBack: undefined,
              disabled: false,
              title: this.$i18next.t('agent:contact.mailactions.layerAction.titlePrint'),
              children: [],
            },
          ];

          // add the dropZone events to add and remove files from the attachment component (even if no right to add files)
          this.getButton(EpticaButtonTypes.PJ).map((button: EpticaButtonActionTypes) => request.addPJEventsForAnswer(button.callBack));

          // enable reply all button if at least one adresses is found.
          let addresses = request.getAllRecipients(this.replyAllBlackList);
          if (!!addresses && addresses.length > 0) {
            this.changeButtonDisableState(EpticaButtonTypes.REPLY_ALL, false);
          }

          this.title = request.moveSubjectInHeader();

          // add the change recipient button into the eptica frame
          // Only if it has the right to do it
          request.addChangeRecipientButton(this.responseId, request.getId(), this);

          // change eptica's buttons
          request.addEditorActionButtons('' + request.getId(), this.actionButtonList);
          request.loadDraftAttachments(callbackAttachmentFiles);
        });

        if (this.displayOWI) {
          // check if a responseType is found
          this.knowledgeBaseService.getReponseType('' + request.getId()).subscribe((reponseType: ISemanticAnalysisResponse) => {
            // Enable the button if the requesType exists
            this.showClearDraft = true;
            this.changeButtonDisableState(EpticaButtonTypes.CLEAR_DRAFT, false);

            request.doInEpticaFrame(() => {
              // get the text value of the html string
              const textValue = reponseType.responseText.replace(/<style.*?<\/style>/g, '').replace(/<[^>]+>/g, '');

              if (textValue.length) {
                request.addTextToEditor(reponseType.responseText, this.$i18next);
              }

              // create the files to upload
              let myFiles: File[] = [];
              if (!!reponseType.attachments) {
                reponseType.attachments.forEach((mdr: IResponseAttachment) => {
                  let arrayOfBlob = new Array<Blob>();
                  arrayOfBlob.push(mdr.blob);
                  myFiles.push(new File(arrayOfBlob, mdr.name));
                });

                // add the uploaded files
                if (myFiles.length > 0) {
                  request.clearDropZone();
                  request.addUploadFiles(myFiles);
                }
              }
            });
          });
        }

        return request;
      })
      .catch(error => {
        this.$log.error(error);
        this.contactDeprecatedService.refuse(this.responseId, 'TECH_FAILURE');
        return this.$q.reject('ECHEC LORS DU CHARGEMENT');
      });
  }
}

export class MailHeaderAnswerActions implements IComponentOptions {
  templateUrl = templateUrl;
  bindings = {
    responseId: '<',
  } as { [binding: string]: string };
  controller = [
    '$log',
    '$q',
    '$i18next',
    'contactDeprecatedService',
    'epticaDeprecatedService',
    'profileService',
    'customerCardsDeprecatedService',
    'modalService',
    'toastDeprecatedService',
    'knowledgeBaseService',
    'configuration',
    'agentDesktopRouter',
    'knowledgeBaseOwiService',
    'translateService',
    MailHeaderAnswerActionsCtrl,
  ];
}

export default MODULE_NAME;
